// General Wellbeing Assessment Questions based on PERMA model (Positive Psychology)
export const wellbeingAssessmentQuestions = [
  {
    id: 1,
    category: "Positive Emotions",
    question: "Over the past week, how often did you experience positive emotions like joy, gratitude, serenity, interest, hope, pride, amusement, inspiration, awe, or love?",
    options: [
      { text: "Never", score: 0, explanation: "No positive emotional experiences" },
      { text: "Rarely", score: 1, explanation: "Occasional positive moments" },
      { text: "Sometimes", score: 2, explanation: "Moderate positive emotional experiences" },
      { text: "Often", score: 3, explanation: "Regular positive emotional experiences" },
      { text: "Very often", score: 4, explanation: "Frequent positive emotional experiences" }
    ]
  },
  {
    id: 2,
    category: "Engagement (Flow)",
    question: "During the past week, how often did you become absorbed in activities where you lost track of time?",
    options: [
      { text: "Never", score: 0, explanation: "No flow experiences" },
      { text: "Rarely", score: 1, explanation: "Occasional engagement" },
      { text: "Sometimes", score: 2, explanation: "Moderate flow experiences" },
      { text: "Often", score: 3, explanation: "Regular deep engagement" },
      { text: "Very often", score: 4, explanation: "Frequent flow states" }
    ]
  },
  {
    id: 3,
    category: "Relationships",
    question: "How satisfied are you with your personal relationships and social connections?",
    options: [
      { text: "Very dissatisfied", score: 0, explanation: "Poor relationship satisfaction" },
      { text: "Dissatisfied", score: 1, explanation: "Below average relationships" },
      { text: "Neutral", score: 2, explanation: "Average relationship satisfaction" },
      { text: "Satisfied", score: 3, explanation: "Good relationship quality" },
      { text: "Very satisfied", score: 4, explanation: "Excellent relationships" }
    ]
  },
  {
    id: 4,
    category: "Meaning & Purpose",
    question: "How meaningful do you find your life? Do you feel your life has purpose and direction?",
    options: [
      { text: "Not meaningful at all", score: 0, explanation: "Lack of life meaning" },
      { text: "Slightly meaningful", score: 1, explanation: "Some sense of purpose" },
      { text: "Moderately meaningful", score: 2, explanation: "Adequate life meaning" },
      { text: "Very meaningful", score: 3, explanation: "Strong sense of purpose" },
      { text: "Extremely meaningful", score: 4, explanation: "Deep life meaning and purpose" }
    ]
  },
  {
    id: 5,
    category: "Accomplishment",
    question: "How often do you feel a sense of accomplishment or achievement in your daily activities?",
    options: [
      { text: "Never", score: 0, explanation: "No sense of achievement" },
      { text: "Rarely", score: 1, explanation: "Occasional accomplishments" },
      { text: "Sometimes", score: 2, explanation: "Moderate achievement feelings" },
      { text: "Often", score: 3, explanation: "Regular sense of accomplishment" },
      { text: "Very often", score: 4, explanation: "Frequent achievement experiences" }
    ]
  },
  {
    id: 6,
    category: "Physical Health",
    question: "How would you rate your overall physical health and energy levels?",
    options: [
      { text: "Very poor", score: 0, explanation: "Significant health concerns" },
      { text: "Poor", score: 1, explanation: "Below average health" },
      { text: "Fair", score: 2, explanation: "Average health status" },
      { text: "Good", score: 3, explanation: "Above average health" },
      { text: "Excellent", score: 4, explanation: "Outstanding physical health" }
    ]
  },
  {
    id: 7,
    category: "Emotional Regulation",
    question: "How well do you manage stress and cope with challenges in your daily life?",
    options: [
      { text: "Very poorly", score: 0, explanation: "Poor stress management" },
      { text: "Poorly", score: 1, explanation: "Struggling with challenges" },
      { text: "Adequately", score: 2, explanation: "Average coping skills" },
      { text: "Well", score: 3, explanation: "Good stress management" },
      { text: "Very well", score: 4, explanation: "Excellent coping abilities" }
    ]
  },
  {
    id: 8,
    category: "Life Satisfaction",
    question: "Overall, how satisfied are you with your life as a whole right now?",
    options: [
      { text: "Completely dissatisfied", score: 0, explanation: "Very low life satisfaction" },
      { text: "Mostly dissatisfied", score: 1, explanation: "Below average satisfaction" },
      { text: "Mixed", score: 2, explanation: "Neutral life satisfaction" },
      { text: "Mostly satisfied", score: 3, explanation: "Good life satisfaction" },
      { text: "Completely satisfied", score: 4, explanation: "Excellent life satisfaction" }
    ]
  },
  {
    id: 9,
    category: "Personal Growth",
    question: "How much do you feel you are growing and developing as a person?",
    options: [
      { text: "Not at all", score: 0, explanation: "No sense of personal growth" },
      { text: "Very little", score: 1, explanation: "Minimal development" },
      { text: "Somewhat", score: 2, explanation: "Moderate personal growth" },
      { text: "Quite a bit", score: 3, explanation: "Strong personal development" },
      { text: "A great deal", score: 4, explanation: "Significant personal growth" }
    ]
  },
  {
    id: 10,
    category: "Self-Acceptance",
    question: "How comfortable are you with yourself, including your strengths and weaknesses?",
    options: [
      { text: "Very uncomfortable", score: 0, explanation: "Poor self-acceptance" },
      { text: "Uncomfortable", score: 1, explanation: "Struggling with self-image" },
      { text: "Neutral", score: 2, explanation: "Average self-acceptance" },
      { text: "Comfortable", score: 3, explanation: "Good self-acceptance" },
      { text: "Very comfortable", score: 4, explanation: "Excellent self-acceptance" }
    ]
  }
];

// Wellbeing level categories with detailed analysis
export const wellbeingLevels = {
  low: {
    range: [0, 16],
    level: "Low Wellbeing",
    color: "from-red-400 to-red-600",
    icon: "😞",
    description: "Your wellbeing scores suggest you may be struggling in several life areas. This is a good time to focus on self-care and consider seeking support.",
    recommendations: [
      "Consider speaking with a counselor or therapist for personalized support",
      "Focus on basic self-care: regular sleep, nutritious meals, and gentle exercise",
      "Start with small, achievable goals to build positive momentum",
      "Connect with supportive friends, family, or community resources",
      "Practice mindfulness or relaxation techniques daily",
      "Consider joining support groups or wellness programs",
      "Evaluate and reduce sources of chronic stress where possible"
    ],
    emergencyLevel: true
  },
  moderate: {
    range: [17, 28],
    level: "Moderate Wellbeing",
    color: "from-orange-400 to-orange-600",
    icon: "😐",
    description: "You have some areas of strength but also areas that could benefit from attention and improvement.",
    recommendations: [
      "Identify your strongest wellbeing areas and build on them",
      "Focus on 1-2 specific areas that scored lowest for targeted improvement",
      "Develop a regular routine that includes activities you enjoy",
      "Strengthen your social connections and support network",
      "Set meaningful short-term and long-term goals",
      "Practice gratitude and mindfulness regularly",
      "Consider lifestyle changes that align with your values and interests"
    ],
    emergencyLevel: false
  },
  good: {
    range: [29, 36],
    level: "Good Wellbeing", 
    color: "from-yellow-400 to-yellow-600",
    icon: "😊",
    description: "You're doing well in most areas of wellbeing with room for continued growth and optimization.",
    recommendations: [
      "Continue practicing the strategies that are working well for you",
      "Explore new activities and experiences to enhance your growth",
      "Share your positive practices with others who might benefit",
      "Set challenging but achievable goals to maintain momentum",
      "Regularly assess and adjust your wellbeing practices",
      "Consider mentoring or helping others as a way to find meaning",
      "Maintain work-life balance and prevent burnout"
    ],
    emergencyLevel: false
  },
  high: {
    range: [37, 40],
    level: "High Wellbeing",
    color: "from-green-400 to-green-600",
    icon: "😄",
    description: "Excellent! You're thriving across multiple dimensions of wellbeing. You're a great example of human flourishing.",
    recommendations: [
      "Maintain your excellent wellbeing practices and routines",
      "Continue to challenge yourself with meaningful goals",
      "Share your knowledge and support others in their wellbeing journey",
      "Stay mindful of maintaining this level during life transitions",
      "Keep exploring new areas of growth and contribution",
      "Consider leadership roles in promoting community wellbeing",
      "Regularly reflect on and appreciate your accomplishments"
    ],
    emergencyLevel: false
  }
};

